import { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import DashboardRedirect from './pages/DashboardRedirect';
import TeacherDashboard from './pages/dashboards/TeacherDashboard';
import ChefDashboard from './pages/dashboards/ChefDashboard';
import CpDashboard from './pages/dashboards/CpDashboard';
import DaDashboard from './pages/dashboards/DaDashboard';
import ScDashboard from './pages/dashboards/ScDashboard';
import DirecteurDashboard from './pages/dashboards/DirecteurDashboard';
import GridView from './pages/GridView';
import ConseillerDashboard from './components/conseiller/ConseillerDashboard';
import ValidationPedagogique from './components/conseiller/ValidationPedagogique';
import RoleManagement from './components/admin/RoleManagement';
import AdminDashboard from './pages/admin/AdminDashboard';
import PlansManagement from './pages/admin/PlansManagement';
import AdminPlanDetail from './pages/admin/AdminPlanDetail';
import ActivityLogs from './pages/admin/ActivityLogs';
import SessionsManagement from './pages/admin/SessionsManagement';
import ProgrammesManagement from './pages/admin/ProgrammesManagement';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './auth/AuthContext';
import { AUTH_EXPIRED_EVENT, clearAuthExpiredNotice, consumeAuthExpiredNotice } from './api/client';

function App() {
  const navigate = useNavigate();
  const { clearSession } = useAuth();
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');
  const [buildUpdateAvailable, setBuildUpdateAvailable] = useState(false);

  useEffect(() => {
    const pendingNotice = consumeAuthExpiredNotice();
    if (pendingNotice) {
      setSessionExpiredMessage(pendingNotice);
    }

    const handleAuthExpired = (event) => {
      const nextMessage = event?.detail?.message || 'Votre session a expire. Veuillez vous reconnecter.';
      clearSession();
      setSessionExpiredMessage(nextMessage);
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, [clearSession]);

  useEffect(() => {
    const getMainBundleUrl = (html) => {
      const match = html.match(/<script[^>]+src=["']([^"']*\/static\/js\/main\.[^"']+\.js)["']/i);
      return match ? new URL(match[1], window.location.origin).href : null;
    };

    const checkForBuildUpdate = async () => {
      try {
        const deployedPage = await fetch('/index.html', { cache: 'no-store' });
        if (!deployedPage.ok) return;

        const deployedBundle = getMainBundleUrl(await deployedPage.text());
        const activeBundle = Array.from(document.scripts)
          .map((script) => script.src)
          .find((src) => /\/static\/js\/main\.[^/]+\.js$/.test(src));

        if (deployedBundle && activeBundle && deployedBundle !== activeBundle) {
          setBuildUpdateAvailable(true);
        }
      } catch {
        // A temporary network issue must not interrupt the application.
      }
    };

    checkForBuildUpdate();
    const intervalId = window.setInterval(checkForBuildUpdate, 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  const handleReconnect = () => {
    clearAuthExpiredNotice();
    setSessionExpiredMessage('');
    navigate('/', { replace: true });
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<Login />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRedirect />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/enseignant"
        element={
          <ProtectedRoute>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/chef"
        element={
          <ProtectedRoute>
            <ChefDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/conseiller"
        element={
          <ProtectedRoute>
            <CpDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/directeur"
        element={
          <ProtectedRoute>
            <DaDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/specialiste"
        element={
          <ProtectedRoute>
            <ScDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/grid/:planId"
        element={
          <ProtectedRoute>
            <GridView />
          </ProtectedRoute>
        }
      />

      <Route
        path="/conseiller"
        element={
          <ProtectedRoute>
            <ConseillerDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/validation-pedagogique/:planId"
        element={
          <ProtectedRoute>
            <ValidationPedagogique />
          </ProtectedRoute>
        }
      />

      <Route
        path="/directeur"
        element={
          <ProtectedRoute>
            <DirecteurDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute>
            <RoleManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <RoleManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/plans"
        element={
          <ProtectedRoute>
            <PlansManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/plan-detail/:planId"
        element={
          <ProtectedRoute>
            <AdminPlanDetail />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/activity"
        element={
          <ProtectedRoute>
            <ActivityLogs />
          </ProtectedRoute>
        }
      />

        <Route
          path="/admin/sessions"
          element={
            <ProtectedRoute>
              <SessionsManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/programmes"
          element={
            <ProtectedRoute>
              <ProgrammesManagement />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>

      {sessionExpiredMessage && (
        <div className="session-modal-overlay" role="presentation">
          <div
            className="session-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="session-expired-title"
          >
            <h2 id="session-expired-title">Session expiree</h2>
            <p>{sessionExpiredMessage}</p>
            <button type="button" onClick={handleReconnect}>
              Se reconnecter
            </button>
          </div>
        </div>
      )}

      {buildUpdateAvailable && (
        <div className="session-modal-overlay" role="presentation">
          <div className="session-modal" role="dialog" aria-modal="true" aria-labelledby="build-update-title">
            <h2 id="build-update-title">Mise a jour disponible</h2>
            <p>Une nouvelle version de VeriPlan est disponible.</p>
            <button type="button" onClick={() => window.location.reload()}>
              Actualiser maintenant
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
